import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Autocomplete, Button, Checkbox, CircularProgress, FormControlLabel, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRotateLeft, faFileCsv } from '@fortawesome/free-solid-svg-icons';
import { TranslationKeys } from '../../../../translations';
import {
  PageWrapperStyled,
  DescriptionStyled,
  FilterCardStyled,
  FilterColumnsStyled,
  FilterColumnStyled,
  CheckboxRowStyled,
  ActionsRowStyled,
} from './ProductInvoicingPage.styles';
import { parseDateString } from './types';
import { useProductInvoicingPage } from './useProductInvoicingPage';

type OptionProps = React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };

// Options can share the same display name, so the list key must rely on a stable id, not the label.
const renderOptionWithKey = (props: OptionProps, key: string, label: React.ReactNode) => {
  const { key: _discardKey, ...optionProps } = props;
  return (
    <li key={key} {...optionProps}>
      {label}
    </li>
  );
};

export const ProductInvoicingPage = () => {
  const intl = useIntl();
  const {
    filters,
    locale,
    owners,
    isLoadingOwners,
    filteredEmployers,
    isLoadingEmployers,
    insurers,
    isLoadingInsurers,
    availableProducts,
    selectedOwner,
    selectedEmployer,
    selectedInsurer,
    selectedProduct,
    isExporting,
    canExport,
    handleOwnerChange,
    handleEmployerChange,
    handleInsurerChange,
    handleProductChange,
    handleDateFromChange,
    handleDateToChange,
    handleSkipZeroResultsChange,
    handleReset,
    handleExport,
  } = useProductInvoicingPage();

  return (
    <PageWrapperStyled>
      <DescriptionStyled variant="body2">
        <FormattedMessage id={TranslationKeys.adminPanel_productInvoicing_description} />
      </DescriptionStyled>

      <FilterCardStyled elevation={0}>
        <FilterColumnsStyled>
          <FilterColumnStyled>
            <Autocomplete
              options={owners}
              loading={isLoadingOwners}
              value={selectedOwner}
              getOptionLabel={option => option.ownerName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => handleOwnerChange(value?.id ?? '')}
              renderOption={(props, option) => renderOptionWithKey(props, option.id, option.ownerName)}
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  label={<FormattedMessage id={TranslationKeys.adminPanel_productInvoicing_owner} />}
                  helperText={<FormattedMessage id={TranslationKeys.validationMessage_mixed_required} />}
                />
              )}
            />

            <Autocomplete
              options={filteredEmployers}
              loading={isLoadingEmployers}
              disabled={!filters.ownerId}
              value={selectedEmployer}
              getOptionLabel={option => option.employerName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => handleEmployerChange(value?.id ?? '')}
              renderOption={(props, option) => renderOptionWithKey(props, option.id, option.employerName)}
              renderInput={params => (
                <TextField
                  {...params}
                  label={<FormattedMessage id={TranslationKeys.adminPanel_productInvoicing_employer} />}
                />
              )}
            />

            <Autocomplete
              options={insurers}
              loading={isLoadingInsurers}
              value={selectedInsurer}
              getOptionLabel={option => option.insurerName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => handleInsurerChange(value?.id ?? '')}
              renderOption={(props, option) => renderOptionWithKey(props, option.id, option.insurerName)}
              renderInput={params => (
                <TextField
                  {...params}
                  label={<FormattedMessage id={TranslationKeys.adminPanel_productInvoicing_insurer} />}
                />
              )}
            />

            <Autocomplete
              options={availableProducts}
              disabled={availableProducts.length === 0}
              value={selectedProduct}
              getOptionLabel={option => option.productName}
              isOptionEqualToValue={(option, value) => option.productId === value.productId}
              onChange={(_, value) => handleProductChange(value?.productId ?? '')}
              renderOption={(props, option) => renderOptionWithKey(props, option.productId, option.productName)}
              renderInput={params => (
                <TextField
                  {...params}
                  label={<FormattedMessage id={TranslationKeys.adminPanel_productInvoicing_product} />}
                />
              )}
            />
          </FilterColumnStyled>

          <FilterColumnStyled>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
              <DatePicker
                label={<FormattedMessage id={TranslationKeys.adminPanel_productInvoicing_dateFrom} />}
                format="yyyy-MM-dd"
                value={parseDateString(filters.dateFrom)}
                onChange={handleDateFromChange}
                slotProps={{ textField: { fullWidth: true } }}
              />

              <DatePicker
                label={<FormattedMessage id={TranslationKeys.adminPanel_productInvoicing_dateTo} />}
                format="yyyy-MM-dd"
                value={parseDateString(filters.dateTo)}
                onChange={handleDateToChange}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>

            <CheckboxRowStyled>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.skipZeroResults}
                    onChange={e => handleSkipZeroResultsChange(e.target.checked)}
                  />
                }
                label={<FormattedMessage id={TranslationKeys.adminPanel_productInvoicing_skipZeroResults} />}
              />
            </CheckboxRowStyled>
          </FilterColumnStyled>
        </FilterColumnsStyled>

        <ActionsRowStyled>
          <Button variant="text" startIcon={<FontAwesomeIcon icon={faArrowRotateLeft} />} onClick={handleReset}>
            <FormattedMessage id={TranslationKeys.adminPanel_productInvoicing_resetFilters} />
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!canExport}
            startIcon={
              isExporting ? <CircularProgress size={16} color="inherit" /> : <FontAwesomeIcon icon={faFileCsv} />
            }
            onClick={handleExport}
          >
            {isExporting
              ? intl.formatMessage({ id: TranslationKeys.adminPanel_productInvoicing_exporting })
              : intl.formatMessage({ id: TranslationKeys.adminPanel_productInvoicing_exportButton })}
          </Button>
        </ActionsRowStyled>
      </FilterCardStyled>
    </PageWrapperStyled>
  );
};
